"use client";
import React, { ReactNode } from "react";

import FormWrapper from "@/ui/Forms/FormWrapper";
import { cn } from "@/utils/cn";

export interface CheckboxOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface FormCheckboxGroupProps {
  values?: string[];
  value?: string[];
  onChange: (values: string[]) => void;
  options: CheckboxOption[];
  label?: string;
  error?: string;
  required?: boolean;
  labelRightComponent?: ReactNode;
  layout?: "horizontal" | "vertical" | "grid" | "pills";
  mode?: "checkbox" | "pills";
  columns?: number;
  selectedKeys?: string[];
  errorClassName?: string;
  disabled?: boolean;
  isBold?: boolean;
}

/**
 * Form checkbox group for selecting multiple options from a list
 */
export const FormCheckboxGroup: React.FC<FormCheckboxGroupProps> = ({
  values,
  value,
  onChange,
  options,
  label,
  error,
  required = false,
  labelRightComponent,
  layout = "vertical",
  mode = "pills",
  columns = 2,
  selectedKeys,
  errorClassName,
  disabled = false,
  isBold = false,
}) => {
  // Use selectedKeys if provided, otherwise use values or value
  const selectedValues = selectedKeys || values || value || [];

  const handleToggle = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];

    onChange(newValues);
  };

  const getLayoutClassName = () => {
    const baseClassName =
      mode === "checkbox" ? "form-checkbox-group-checkbox" : "";

    switch (layout) {
      case "horizontal":
        return cn("form-checkbox-group-horizontal", baseClassName);
      case "grid":
        return cn("form-checkbox-group-grid", baseClassName);
      case "pills":
        return cn("form-checkbox-group-pills", baseClassName);
      case "vertical":
      default:
        return cn("form-checkbox-group-vertical", baseClassName);
    }
  };

  return (
    <FormWrapper
      label={label}
      error={error}
      required={required}
      labelRight={labelRightComponent}
      errorClassName={errorClassName}
    >
      <div
        className={cn("form-checkbox-group-base", getLayoutClassName())}
        style={
          layout === "grid"
            ? { gridTemplateColumns: `repeat(${columns}, 1fr)` }
            : undefined
        }
      >
        {options.map((option) => (
          <div
            key={option.value}
            className={cn(
              !disabled && "form-checkbox-item",
              mode === "checkbox" && "form-checkbox-item-checkbox",
              selectedValues.includes(option.value) &&
                "form-checkbox-item-selected",
              isBold && "form-select-pill-option group",
              isBold && selectedValues.includes(option.value) && "active",
            )}
            onClick={() => handleToggle(option.value)}
          >
            <div className="form-checkbox-item-content">
              {mode === "checkbox" && (
                <input
                  disabled={disabled}
                  type="checkbox"
                  checked={selectedValues.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                  className="form-checkbox-input"
                />
              )}
              {option.icon && (
                <span className="form-checkbox-item-icon">{option.icon}</span>
              )}
              <span
                className={cn(
                  disabled && "!cursor-default",
                  isBold
                    ? "font-medium text-[16px] text-[#323232] group-hover:text-red transition-colors duration-300"
                    : "form-checkbox-item-label",
                  isBold &&
                    selectedValues.includes(option.value) &&
                    "!text-red font-bold",
                )}
              >
                {option.label}
              </span>
            </div>
          </div>
        ))}
      </div>
    </FormWrapper>
  );
};

export default FormCheckboxGroup;
